import { Component } from '@angular/core';
import { NameResolveService, ResolveResponse } from 'src/app/services/name-resolve-service';
import { UserService } from 'src/app/services/user-service';
import { QuickSignupService } from 'src/app/services/quick-signup-service';
import { PlatexUserAlert } from 'src/app/models/platexUserAlert';

/* The Watchlist. A name watch rides the existing term-based UserAlert model: an
   alert on the name fires when a registration matching it appears or comes to
   market. That is genuinely what the backend supports, so the copy promises
   exactly that — not verified-sale events, which the alert stream does not
   distinguish. A name that cannot be written is rejected (nothing could ever
   spell it). Self-contained: validation, signup, alert creation and all states. */
@Component({
  selector: 'pla-names-watchlist',
  templateUrl: './names-watchlist.component.html',
  styleUrls: ['./names-watchlist.component.scss'],
})
export class NamesWatchlistComponent {
  public watchName = '';
  public watchEmail = '';
  public status: 'idle' | 'checking' | 'saving' | 'added' | 'duplicate' | 'invalid' | 'unwritable' | 'error' = 'idle';
  public message = '';

  constructor(
    private resolveService: NameResolveService,
    private userService: UserService,
    private signupService: QuickSignupService
  ) {}

  public submit(): void {
    const name = (this.watchName || '').trim();
    if (name.length < 2) {
      this.status = 'invalid';
      this.message = 'Enter the name you want to watch.';
      return;
    }
    this.status = 'checking';
    this.message = '';
    // validate the name is actually writable before creating a watch
    this.resolveService.resolve(name).subscribe(
      (res: ResolveResponse) => {
        if (res.outcome === 'ROMANISATION_REQUIRED') {
          this.status = 'invalid';
          this.message = res.message || 'Enter the romanised form of the name.';
          return;
        }
        if (res.outcome === 'INVALID_INPUT') {
          this.status = 'invalid';
          this.message = res.message || 'Enter a valid name.';
          return;
        }
        if (res.verdict === 'cannot-write') {
          this.status = 'unwritable';
          this.message = `${this.titleCase(res.input.normalised)} cannot be written on a British plate, so there is nothing to watch.`;
          return;
        }
        this.createWatch(res.input.normalised);
      },
      () => {
        this.status = 'error';
        this.message = 'We could not check that name just now. Please try again.';
      }
    );
  }

  private createWatch(normalisedName: string): void {
    this.status = 'saving';
    const email = (this.watchEmail || '').trim() || undefined;
    // ensure a session (anonymous capture -> account), same as Number Ones
    this.signupService.quickSignup(
      `names register watch ${normalisedName}`,
      email,
      (ok: boolean) => {
        if (!ok) {
          this.status = 'idle';
          return;
        }
        this.addNameAlert(normalisedName);
      },
      () => this.addNameAlert(normalisedName)
    );
    setTimeout(() => {
      if (this.status === 'saving') this.status = 'idle';
    }, 30000);
  }

  private addNameAlert(normalisedName: string): void {
    if (this.status === 'added') return;
    // duplicate guard: an active alert on this term already exists
    const existing = (this.userService.PlatexUserAlerts || []).find(
      (a) => (a.term || '').toUpperCase() === normalisedName
    );
    if (existing) {
      this.status = 'duplicate';
      this.message = `You are already watching ${this.titleCase(normalisedName)}.`;
      return;
    }
    const alert = new PlatexUserAlert();
    alert.term = normalisedName;
    alert.status = 'active';
    this.userService.addAlert(
      alert,
      () => {
        this.status = 'added';
        this.message = `We will email you when a registration for ${this.titleCase(normalisedName)} appears or comes to market.`;
      },
      (message: string) => {
        this.status = 'error';
        this.message = message || 'We could not add that watch right now. Please try again.';
      }
    );
  }

  private titleCase(s: string): string {
    return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
  }
}
